import { SUMMARY_ENTITY_LABELS, TREE_UNCROPPED_LABEL } from 'constants/uiConstants';
import type { GUnit } from 'interfaces/IGlobalCatalogue';
import type { IInput } from 'interfaces/IInput';
import { InputTaskCategory, type IInputTask } from 'interfaces/IInputTask';
import {
  InputStepType,
  type IInputStep,
  type IInputStepProduction,
  type IInputStepRateCost,
} from 'interfaces/IInputStep';
import type {
  CashflowPeriod,
  CashflowYearMode,
  SummaryEntityCategory,
} from '../../../../../redux/slices/InsightsSlice';
import { resolveAreaHa, resolveEffectiveRate } from '../insightUtils';
import {
  buildCashflowPeriods,
  CASHFLOW_COST_CATEGORIES,
  resolveCashflowPeriodIndex,
  STEP_TYPE_TO_CASHFLOW_CATEGORY,
  type CashflowPeriodColumn,
} from './cashflowUtils';

/** One line of the cashflow table. `values` is `[Total, No Date, ...periods]`, matching computeSummary.ts's Total/No-date/monthly column convention. */
export interface CashflowLine {
  key: string;
  label: string;
  values: number[];
}

export interface CashflowCategoryGroup {
  category: SummaryEntityCategory;
  label: string;
  /** Category subtotal — sum of `products`. */
  total: CashflowLine;
  /** Individual product line items, only rendered in Detailed mode. */
  products: CashflowLine[];
}

export interface CashflowResult {
  periods: CashflowPeriodColumn[];
  /** One line per crop, only rendered in Detailed mode. */
  incomeLines: CashflowLine[];
  totalIncome: CashflowLine;
  expenditureCategories: CashflowCategoryGroup[];
  totalExpenditure: CashflowLine;
  balance: CashflowLine;
  /** Running cumulative balance, seeded by the No Date column as the opening pre-period amount. Its Total column isn't meaningful (a running balance can't be summed) — callers should not display values[0] for this line. */
  cashFlow: CashflowLine;
}

const sumValues = (a: number[], b: number[]): number[] =>
  a.map((v, i) => v + (b[i] ?? 0));

/**
 * Which column a step's amount belongs in, with a fallback for steps that
 * carry no date of their own.
 *
 * A step normally resolves a precise date via `step.endDate ?? task.endDate
 * ?? input.endDate`. Genuinely undated data (no date anywhere on that
 * chain) is expected only from imported historical/legacy records — a plan
 * created in-app always has task/step dates, or at minimum an
 * `input.endDate`. For that legacy case, `input.startDate` is used as a
 * last-resort fallback purely to confine the amount to the correct year —
 * it still lands in the "No Date" column (we don't know its specific
 * month), but only for the year its startDate falls in, rather than
 * reappearing in every year the user views. When even startDate is
 * missing, there's no date information anywhere on the plan at all, and
 * the amount unavoidably shows in every year's No Date column.
 */
const resolveBucket = (
  step: IInputStep,
  task: IInputTask,
  input: IInput,
  periods: CashflowPeriodColumn[]
): number | 'noDate' | 'outOfWindow' => {
  const preciseDate = step.endDate ?? task.endDate ?? input.endDate;
  if (preciseDate) return resolveCashflowPeriodIndex(preciseDate, periods);

  if (!input.startDate) return 'noDate';
  const fallbackBucket = resolveCashflowPeriodIndex(input.startDate, periods);
  // The fallback only ever answers "this year" or "not this year" — never a
  // specific month/quarter, since a plan's startDate isn't really when this
  // particular cost/income occurred.
  return fallbackBucket === 'outOfWindow' ? 'outOfWindow' : 'noDate';
};

/**
 * Compute the Cashflow lens's Income/Expenditure/Balance/Cash Flow table for
 * the given scope, task category (Plan/Actual), and year window.
 *
 * Pure function — no side effects, no hooks, no Redux. Follows the same
 * architectural pattern as computeGrossMargin.ts/computeSummary.ts, but adds
 * its own year-aware month/quarter bucketing (see cashflowUtils.ts) since no
 * existing compute module in this subsystem tracks a calendar year, only a
 * bare 0-11 month index.
 */
export const computeCashflow = (
  inputs: IInput[],
  category: InputTaskCategory,
  period: CashflowPeriod,
  year: number,
  yearMode: CashflowYearMode,
  gUnits: GUnit[]
): CashflowResult => {
  const periods = buildCashflowPeriods(year, yearMode, period);
  const columnCount = 2 + periods.length;
  const zeros = (): number[] => new Array<number>(columnCount).fill(0);

  const addValue = (
    values: number[],
    amount: number,
    bucket: number | 'noDate' | 'outOfWindow'
  ): void => {
    if (bucket === 'outOfWindow') return;
    values[0] += amount;
    if (bucket === 'noDate') {
      values[1] += amount;
    } else {
      values[2 + bucket] += amount;
    }
  };

  const getOrCreate = <K,>(map: Map<K, number[]>, key: K): number[] => {
    const existing = map.get(key);
    if (existing) return existing;
    const values = zeros();
    map.set(key, values);
    return values;
  };

  const incomeMap = new Map<string, number[]>();
  const expenditureMap = new Map<SummaryEntityCategory, Map<string, number[]>>();

  for (const input of inputs) {
    for (const task of input.tasks) {
      if (task.category !== category) continue;

      for (const step of task.steps) {
        const bucket = resolveBucket(step, task, input, periods);
        if (bucket === 'outOfWindow') continue;

        if (step.stepType === InputStepType.Production) {
          const s = step as IInputStepProduction;
          const yieldVal = s.yield ?? 0;
          const price = s.price ?? 0;
          if (yieldVal <= 0 || price <= 0) continue;
          const areaHa = resolveAreaHa(step, task, input, gUnits);
          if (areaHa <= 0) continue;

          const amount = yieldVal * price * areaHa;
          const cropName = input.crop.cropName || TREE_UNCROPPED_LABEL;
          addValue(getOrCreate(incomeMap, cropName), amount, bucket);
          continue;
        }

        const cat = STEP_TYPE_TO_CASHFLOW_CATEGORY[step.stepType];
        if (!cat) continue;

        const s = step as unknown as IInputStepRateCost;
        const cost = s.cost ?? 0;
        const effectiveRate = resolveEffectiveRate(step, task, gUnits);
        if (cost <= 0 || effectiveRate === null || effectiveRate <= 0) continue;
        const areaHa = resolveAreaHa(step, task, input, gUnits);
        if (areaHa <= 0) continue;

        const amount = cost * effectiveRate * areaHa;
        const productKey = step.item || 'Unspecified';
        const catMap = expenditureMap.get(cat) ?? new Map<string, number[]>();
        expenditureMap.set(cat, catMap);
        addValue(getOrCreate(catMap, productKey), amount, bucket);
      }
    }
  }

  const incomeLines: CashflowLine[] = Array.from(incomeMap.entries())
    .map(([label, values]) => ({ key: label, label, values }))
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));

  const totalIncome: CashflowLine = {
    key: 'totalIncome',
    label: 'Total Income',
    values: incomeLines.reduce((acc, line) => sumValues(acc, line.values), zeros()),
  };

  const expenditureCategories: CashflowCategoryGroup[] = CASHFLOW_COST_CATEGORIES
    .map((cat): CashflowCategoryGroup | null => {
      const catMap = expenditureMap.get(cat);
      if (!catMap) return null;

      const products = Array.from(catMap.entries())
        .map(([label, values]) => ({ key: label, label, values }))
        .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));

      const label = SUMMARY_ENTITY_LABELS[cat] ?? cat;
      return {
        category: cat,
        label,
        total: {
          key: cat,
          label,
          values: products.reduce((acc, p) => sumValues(acc, p.values), zeros()),
        },
        products,
      };
    })
    .filter((g): g is CashflowCategoryGroup => g !== null);

  const totalExpenditure: CashflowLine = {
    key: 'totalExpenditure',
    label: 'Total Expenditure',
    values: expenditureCategories.reduce(
      (acc, g) => sumValues(acc, g.total.values),
      zeros()
    ),
  };

  const balance: CashflowLine = {
    key: 'balance',
    label: 'Balance',
    values: totalIncome.values.map((v, i) => v - totalExpenditure.values[i]),
  };

  // Running cumulative balance — seeded by the No Date column (undated
  // income/costs are treated as occurring before period 1, matching the
  // reference mockup where undated totals flow straight into the opening
  // Cash Flow figure), then each period's balance accumulates on top.
  const cashFlowValues = zeros();
  cashFlowValues[1] = balance.values[1];
  let running = balance.values[1];
  for (let i = 0; i < periods.length; i++) {
    running += balance.values[2 + i];
    cashFlowValues[2 + i] = running;
  }
  const cashFlow: CashflowLine = {
    key: 'cashFlow',
    label: 'Cash Flow (Open=$0)',
    values: cashFlowValues,
  };

  return {
    periods,
    incomeLines,
    totalIncome,
    expenditureCategories,
    totalExpenditure,
    balance,
    cashFlow,
  };
};
